<template>
  <multiselect
    id="ajax"
    v-model="selectedEntities"
    :label="labelField"
    :track-by="trackByField"
    :placeholder="placeholder"
    open-direction="bottom"
    :options="entities"
    :multiple="multiple"
    :loading="isLoading"
    :clear-on-select="clearOnSelect"
    :close-on-select="closeOnSelect"
    :searchable="searchable"
    :internal-search="internalSearch"
    :show-no-results="showNoResults"
    :hide-selected="hideSelected"
    :taggable="taggable"
    :tag-placeholder="tagPlaceholder"
    :max-height="maxHeight"
    @tag="addTag"
    @search-change="debouncedSearchEntity"
    @input="handleInput"
    @remove="handleRemove"
  >
    <span
      slot="afterList"
      v-observe-visibility="onVisibilityChanged"
    >
      <hr>
    </span>
    <!-- Pass down slots from parent component -->
    <template v-for="(_, name) in $slots" v-slot:[name]="slotData">
      <slot :name="name" v-bind="slotData"></slot>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from "vue-multiselect";
import { isEmpty, debounce } from "lodash";
import { ObserveVisibility } from "vue-observe-visibility";
import axios from "axios";

export default {
  name: "EntitySelect",
  components: { Multiselect },
  directives: { ObserveVisibility },
  props: {
    // API configuration
    searchUrl: {
      type: String,
      required: true
    },
    // Entity configuration
    labelField: {
      type: String,
      default: "name"
    },
    trackByField: {
      type: String,
      default: "name"
    },
    entityType: {
      type: String,
      required: true,
      validator: (value) => ["company", "position", "tag"].includes(value)
    },
    // UI configuration
    placeholder: {
      type: String,
      default: "Type to search"
    },
    tagPlaceholder: {
      type: String,
      default: "Enter to add this item"
    },
    multiple: {
      type: Boolean,
      default: true
    },
    maxHeight: {
      type: Number,
      default: 600
    },
    // Behavior configuration
    minSearchLength: {
      type: Number,
      default: 2
    },
    debounceTime: {
      type: Number,
      default: 500
    },
    maxWaitTime: {
      type: Number,
      default: 1000
    },
    paginationSize: {
      type: Number,
      default: 20
    },
    // Multiselect options
    clearOnSelect: {
      type: Boolean,
      default: false
    },
    closeOnSelect: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: true
    },
    internalSearch: {
      type: Boolean,
      default: false
    },
    showNoResults: {
      type: Boolean,
      default: false
    },
    hideSelected: {
      type: Boolean,
      default: true
    },
    taggable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      entityTags: [],
      entityFilters: [],
      selectedEntities: [],
      entities: [],
      isLoading: false,
      searchQuery: null,
      page: 1,
      axiosCancelSource: null
    };
  },
  watch: {
    entityTags(tags) {
      this.$emit("input", tags.join(" "));
    },
    entityFilters(filters) {
      this.$emit("update-filter", filters);
    }
  },
  created() {
    // Debounce setup - keeps methods clean, assign to Vue's instance
    this.debouncedSearchEntity = debounce(this.searchEntityHandler, this.debounceTime, {
      maxWait: this.maxWaitTime
    });
  },
  methods: {
    /** Returns currently selected tag strings. */
    getSelectedTags() {
      return this.entityTags;
    },
    /** Adds a new entity tag and pushes to selection. */
    addTag(newTag) {
      if (
        newTag.length < this.minSearchLength ||
        this.selectedEntities.some(item => item[this.labelField] === newTag)
      ) {
        return;
      }
      this.entityTags.push(newTag);
      this.selectedEntities.push({
        [this.labelField]: newTag,
        id: `tag_${newTag.slice(0, 2)}${Math.floor(Math.random() * 10000000)}`,
        type: "tag"
      });
    },
    /** Emits only entity names (not tag items) for parent filter. */
    handleInput(options) {
      this.entityFilters = options
        .filter(option => option.type === undefined)
        .map(option => option[this.labelField]);
    },
    /** Removes tag from entityTags if the removed option is a tag. */
    handleRemove(removedOption) {
      if (removedOption.type === "tag") {
        this.entityTags = this.entityTags.filter(
          tag => tag !== removedOption[this.labelField]
        );
      }
    },
    /** Debounced handler for search changes. */
    searchEntityHandler(searchQuery) {
      if (isEmpty(searchQuery) || searchQuery.length < this.minSearchLength) {
        this.entities = [];
        return;
      }
      // Reset params
      this.searchQuery = searchQuery;
      this.entities = [];
      this.page = 1;
      this.loadEntities(searchQuery, 1);
    },
    /** Loads entities from API with search, handles cancelation and pagination. */
    async loadEntities(searchQuery, pageNum = this.page) {
      this.isLoading = true;
      // Cancel any pending requests
      if (this.axiosCancelSource) {
        this.axiosCancelSource.cancel("Operation canceled due to new request.");
      }
      this.axiosCancelSource = axios.CancelToken.source();
      try {
        const response = await axios.get(this.searchUrl, {
          cancelToken: this.axiosCancelSource.token,
          params: {
            search: searchQuery,
            pagination: this.paginationSize,
            page: pageNum
          }
        });
        
        // Process response data based on entity type
        let responseData = response.data.data;
        if (this.entityType === 'position') {
          // Transform position data format if needed
          responseData = responseData.map((item, i) => ({
            id: i,
            [this.labelField]: item.position
          }));
        }
        
        this.entities = [...this.entities, ...responseData];
      } catch (e) {
        if (!axios.isCancel(e)) {
          // Handle errors except cancellation
          console.error(`Error loading ${this.entityType}s`, e);
        }
      } finally {
        this.isLoading = false;
      }
    },
    /** Loads next page of entities for infinite scroll. */
    loadMore() {
      this.page += 1;
      this.loadEntities(this.searchQuery, this.page);
    },
    /** Handler for visibility change (infinite scroll trigger). */
    onVisibilityChanged(isVisible) {
      if (isVisible) {
        this.loadMore();
      }
    },
    /** Returns display string for additional selection count. */
    limitText(count) {
      return `and ${count} other items`;
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
